import classNames from "classnames";
import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { LuSparkles, LuX } from "react-icons/lu";

import {
  AiPurpose,
  AiWriteAction,
  AiWriteResponse,
  useAiWriteMutation,
} from "../../slices/AiSlice";
import {
  aiErrorMessage,
  plainTextToQuillHtml,
  quillHtmlToPlainText,
} from "./ai.utils";
import { useAiStatus } from "./useAiStatus";

type Props = {
  purpose: AiPurpose;
  /** The editor's current content (Quill HTML, or plain text with format "text"). */
  getHtml: () => string;
  /** Replaces the editor's content (HTML, or plain text with format "text"). */
  onUseHtml: (value: string) => void;
  format?: "html" | "text";
  subject?: string;
  /** Fills an empty subject with the drafted one. */
  onSubject?: (subject: string) => void;
  workId?: string;
  customerId?: string;
  /** Open the popover above the button (e.g. a message box at the bottom). */
  placement?: "bottom" | "top";
  align?: "left" | "right";
  /** Icon-only button (toolbars). */
  compact?: boolean;
};

type QuickAction = { action: AiWriteAction; label: string };

const QUICK_ACTIONS: QuickAction[] = [
  { action: "improve", label: "Improve" },
  { action: "shorten", label: "Shorten" },
  { action: "friendlier", label: "Make friendlier" },
  { action: "formal", label: "More formal" },
  { action: "fix", label: "Fix spelling & grammar" },
];
const NOTE_ACTIONS: AiWriteAction[] = ["improve", "shorten", "fix"];

const WIDTH = 380;

type Result = AiWriteResponse & { action: AiWriteAction };

export const Spinner: FC<{ className?: string }> = ({ className }) => (
  <span
    aria-hidden={"true"}
    className={classNames(
      "inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#DCD5FF] border-t-[#6B4EFF]",
      className,
    )}
  />
);

/**
 * "Write with AI": drafts or polishes the text of an email or note. Only
 * shown when the firm has AI switched on. Nothing changes in the editor until
 * the person clicks "Use this".
 */
export const WriteWithAi: FC<Props> = ({
  purpose,
  getHtml,
  onUseHtml,
  format = "html",
  subject,
  onSubject,
  workId,
  customerId,
  placement = "bottom",
  align = "right",
  compact,
}) => {
  const { isOn, orgId, status } = useAiStatus();
  const [aiWrite, { isLoading }] = useAiWriteMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<AiWriteAction | null>(null);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const currentText = () => {
    const value = getHtml() || "";
    return format === "html" ? quillHtmlToPlainText(value) : value.trim();
  };
  const isEmpty = isOpen && !currentText();

  const place = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(WIDTH, window.innerWidth - 16);
    const left = Math.max(
      8,
      Math.min(
        align === "right" ? rect.right - width : rect.left,
        window.innerWidth - width - 8,
      ),
    );
    setPosition(
      placement === "top"
        ? { left, width, bottom: window.innerHeight - rect.top + 6 }
        : { left, width, top: rect.bottom + 6 },
    );
  }, [align, placement]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isOpen, place]);

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popoverRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    // Escape closes only the popover, not the window it sits in.
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [isOpen]);

  if (!isOn) return null;

  const run = async (action: AiWriteAction) => {
    const text = currentText();
    if (action === "draft" ? !instruction.trim() : !text) return;
    setLastAction(action);
    setError(null);
    setResult(null);
    try {
      const response = await aiWrite({
        orgId,
        action,
        purpose,
        text: text || undefined,
        instruction: action === "draft" ? instruction.trim() : undefined,
        subject: subject?.trim() || undefined,
        workId: workId || undefined,
        customerId: customerId || undefined,
      }).unwrap();
      setResult({ ...response, action });
    } catch (caught) {
      setError(aiErrorMessage(caught));
    }
  };

  const applyResult = () => {
    if (!result) return;
    onUseHtml(
      format === "html" ? plainTextToQuillHtml(result.text) : result.text,
    );
    if (
      result.action === "draft" &&
      result.subject?.trim() &&
      onSubject &&
      !subject?.trim()
    ) {
      onSubject(result.subject.trim());
    }
    setResult(null);
    setInstruction("");
    setIsOpen(false);
  };

  const discard = () => {
    setResult(null);
    setError(null);
  };

  const actions = QUICK_ACTIONS.filter(
    (item) => purpose === "email" || NOTE_ACTIONS.includes(item.action),
  );
  const left =
    status && status.limitPerDay > 0
      ? Math.max(0, status.limitPerDay - status.usedToday)
      : null;

  const popover = (
    <div
      ref={popoverRef}
      role={"dialog"}
      aria-label={"Write with AI"}
      style={position}
      className={
        "fixed z-[1000] rounded-[14px] border border-[#EDEFF3] bg-white font-Manrope shadow-[0_18px_50px_rgba(20,22,34,0.22)]"
      }>
      <div
        className={
          "flex items-center gap-2 border-b border-[#EDEFF3] px-4 py-3 text-[13.5px] font-extrabold text-[#1F2433]"
        }>
        <span
          className={
            "flex h-6 w-6 items-center justify-center rounded-md bg-[#F0EDFF] text-[#6B4EFF]"
          }>
          <LuSparkles size={14} />
        </span>
        <span className={"flex-1"}>Write with AI</span>
        {left !== null && (
          <span className={"text-[11px] font-semibold text-[#8A90A2]"}>
            {left} left today
          </span>
        )}
        <button
          type={"button"}
          aria-label={"Close"}
          onClick={() => setIsOpen(false)}
          className={
            "flex h-6 w-6 items-center justify-center rounded-md text-[#8A90A2] hover:bg-[#F4F5F8]"
          }>
          <LuX size={14} />
        </button>
      </div>

      <div className={"px-4 py-3.5"}>
        {isLoading ? (
          <div
            role={"status"}
            className={
              "flex items-center gap-2.5 rounded-[10px] bg-[#F7F5FF] px-3.5 py-4 text-[13.5px] font-semibold text-[#5B3FE6]"
            }>
            <Spinner />
            Writing…
          </div>
        ) : result ? (
          <div>
            <div
              className={
                "mb-1.5 text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A90A2]"
              }>
              Suggestion
            </div>
            <div
              className={
                "max-h-[260px] overflow-y-auto rounded-[10px] border border-[#DCE2EA] bg-[#FBFBFD] px-3.5 py-3 text-[13.5px] leading-[1.55] text-[#1F2433]"
              }>
              {result.action === "draft" && result.subject && (
                <div className={"mb-2 border-b border-[#EDEFF3] pb-2"}>
                  <span className={"text-[#8A90A2]"}>Subject: </span>
                  <span className={"font-semibold"}>{result.subject}</span>
                </div>
              )}
              {format === "html" ? (
                <div
                  className={
                    "[&_li]:ml-4 [&_li]:list-disc [&_p]:min-h-[1em] [&_ul]:my-1"
                  }
                  // Escaped by plainTextToQuillHtml.
                  dangerouslySetInnerHTML={{
                    __html: plainTextToQuillHtml(result.text),
                  }}
                />
              ) : (
                <div className={"whitespace-pre-wrap break-words"}>
                  {result.text}
                </div>
              )}
            </div>
            <div className={"mt-3 flex items-center gap-2"}>
              <button
                type={"button"}
                onClick={applyResult}
                className={
                  "h-8 rounded-lg bg-[#6B4EFF] px-3.5 text-[12.5px] font-bold text-white hover:bg-[#5A3EEB]"
                }>
                Use this
              </button>
              <button
                type={"button"}
                onClick={() => lastAction && void run(lastAction)}
                className={
                  "h-8 rounded-lg border border-[#DCE2EA] px-3 text-[12.5px] font-semibold text-[#3A4256] hover:bg-[#F7F8FA]"
                }>
                Try again
              </button>
              <button
                type={"button"}
                onClick={discard}
                className={
                  "h-8 rounded-lg px-3 text-[12.5px] font-semibold text-[#5B6678] hover:bg-[#F4F5F8]"
                }>
                Discard
              </button>
            </div>
          </div>
        ) : (
          <div>
            <label
              htmlFor={"ai-instruction"}
              className={"mb-1.5 block text-[12.5px] font-bold text-[#1F2433]"}>
              Describe what you want to write
            </label>
            <div className={"flex items-start gap-2"}>
              <textarea
                id={"ai-instruction"}
                autoFocus
                rows={2}
                value={instruction}
                placeholder={
                  purpose === "email"
                    ? "e.g. Remind the client we still need their T4 slips"
                    : "e.g. Client called, sending documents Friday"
                }
                onChange={(event) => setInstruction(event.target.value)}
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void run("draft");
                  }
                }}
                className={
                  "min-h-[56px] flex-1 resize-none rounded-lg border border-[#DCE2EA] px-3 py-2 text-[13px] text-[#1F2433] outline-none placeholder:text-[#A2A8B8] focus:border-[#6B4EFF]"
                }
              />
              <button
                type={"button"}
                disabled={!instruction.trim()}
                onClick={() => void run("draft")}
                className={
                  "h-9 shrink-0 rounded-lg bg-[#6B4EFF] px-3.5 text-[12.5px] font-bold text-white hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:opacity-50"
                }>
                Draft
              </button>
            </div>
            <div
              className={
                "mb-1.5 mt-3.5 text-[11px] font-bold uppercase tracking-[0.04em] text-[#8A90A2]"
              }>
              Or change the current text
            </div>
            <div className={"flex flex-wrap gap-1.5"}>
              {actions.map((item) => (
                <button
                  key={item.action}
                  type={"button"}
                  disabled={isEmpty}
                  title={isEmpty ? "Write something first" : undefined}
                  onClick={() => void run(item.action)}
                  className={
                    "disabled:opacity-45 h-8 rounded-full border border-[#DCE2EA] bg-white px-3 text-[12.5px] font-semibold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#5B3FE6] disabled:cursor-not-allowed disabled:hover:border-[#DCE2EA] disabled:hover:text-[#3A4256]"
                  }>
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && !isLoading && (
          <div
            role={"alert"}
            className={
              "mt-3 rounded-lg border border-[#F6C9CB] bg-[#FDF1F1] px-3 py-2 text-[12.5px] text-[#B4262C]"
            }>
            {error}
          </div>
        )}
        <div className={"mt-3 text-[11px] text-[#8A90A2]"}>
          AI can make mistakes. Check before sending.
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        type={"button"}
        aria-label={"Write with AI"}
        aria-expanded={isOpen}
        title={compact ? "Write with AI" : undefined}
        onClick={() => setIsOpen((open) => !open)}
        className={
          compact
            ? classNames(
                "my-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg hover:bg-[#F4F2FE] hover:text-[#6B4EFF]",
                isOpen ? "bg-[#F4F2FE] text-[#6B4EFF]" : "text-gray-500",
              )
            : "inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[12.5px] font-semibold text-[#6B4EFF] hover:bg-[#F4F2FF]"
        }>
        <LuSparkles size={compact ? 18 : 14} />
        {!compact && "Write with AI"}
      </button>
      {isOpen && createPortal(popover, document.body)}
    </>
  );
};
